import { IModel, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { format, sanitizeAndFormatString } from '../../shared/utils/string.util';
import { BaseMultiInputStreamTransformation } from './base-multi-input-stream-transformation';
import { DataFlowExpression } from './dataflowexpression';
import { Owned } from '../base/named';
import { convertColumnRefToDataFlowColumn, convertParametersToArguments, convertCacheSinksToArguments } from './dataflow-expression-utilities';
import { DataFlowExpressionFunctions } from './dataflow-expression-functions';
import { updateIdentifiers, asyncUpdateIdentifiers } from './dataflow-expression-update-identifiers';
import { BooleanDataFlowType } from './dataflow-datatype';
import { GlobalVariables } from 'app/services/globals';

@Metamodel.model({
    name: 'exists',
    display: [stringResources.DataFlowExistsTransformationLabel, stringResources.DataFlowExistsTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        }
    },
    validator: BaseMultiInputStreamTransformation.prototype.inputStreamValidate
})
export class ExistsTransformation extends BaseMultiInputStreamTransformation {
    public static readonly MODEL: IModel;

    /**
     * Property to distinguish either join or anti-join, default value is false.
     */
    public negate?: boolean = false;
    public useCustomExpression: boolean = false;

    @Metamodel.attribute({
        display: stringResources.Expression,
        constraint: {
            validator: ExistsTransformation.validateCustomExpression
        }
    })
    public customExpression: DataFlowExpression = this._createExpression();

    public get usesConditionExpressions(): boolean {
        return !this.useCustomExpression;
    }

    public static validateCustomExpression(transformation: ExistsTransformation): IViolation[] {
        const violations: IViolation[] = [];
        if (transformation.useCustomExpression) {
            if (!transformation.customExpression.expression) {
                violations.push({
                    context: transformation,
                    message: () => sanitizeAndFormatString(stringResources.ExistsNoCustomExpression, transformation.name)
                });
                return violations;
            } else if (!transformation.customExpression.returnsCorrectType) {
                violations.push({
                    context: transformation,
                    message: () => stringResources.DataFlow_ShouldReturnBoolean
                });
                return violations;
            }
        }
        return undefined;
    }

    public getExpressions(): DataFlowExpression[] {
        return this.useCustomExpression ? [this.customExpression] : super.getExpressions();
    }

    protected _outputColumns(): DataFlowColumn[] {
        return this.inputStreams.length > 0 ? this.inputStreams[0].producer.outputColumns() : [];
    }

    protected getGeneratedDescription(): string {
        // TODO - ans - Change description generation with change in the implementation of the Exists.
        if (this.inputStreams.length === 2) {
            const usedColumns: string[] = [];
            this.conditionExpressions.forEach((conditionExpression) => {
                usedColumns.push(...conditionExpression.leftExpression.argumentsUsed.dataFlowColumns.map((dataFlowColumn) => dataFlowColumn.column.name));
                usedColumns.push(...conditionExpression.rightExpression.argumentsUsed.dataFlowColumns.map((dataFlowColumn) => dataFlowColumn.column.name));
            });
            if (usedColumns.length) {
                return format(this.negate ? stringResources.ExistsDescriptionWithNegate : stringResources.ExistsDescriptionWithoutNegate,
                    this.inputStreams[0].name, this.inputStreams[1].name, usedColumns.join(', '));
            } else {
                return stringResources.ExistNoColumnDescription;
            }
        }
        return stringResources.ExistNoSecondStreamDescription;
    }

    protected updateInputTransformationCacheInternal(): void {
        // Handle all expressions other than custom expressions
        super.updateInputTransformationCacheInternal();
        if (this.useCustomExpression) {
            if (GlobalVariables.validateDataFlowAPI) {
                asyncUpdateIdentifiers({
                    dataFlowColumns: convertColumnRefToDataFlowColumn(this.distinctInputColumnRefs),
                    dataFlowFunctions: DataFlowExpressionFunctions.ALL_FUNCTIONS,
                    dataFlowParameters: convertParametersToArguments(this.dataflow.parameters),
                    dataFlowTransientColumns: convertColumnRefToDataFlowColumn(this.transientColumnRefs),
                    dataFlowCacheSinks: convertCacheSinksToArguments(this.dataflow.cacheSinks)
                }, this.customExpression, this);
            } else {
                updateIdentifiers({
                    dataFlowColumns: convertColumnRefToDataFlowColumn(this.distinctInputColumnRefs),
                    dataFlowFunctions: DataFlowExpressionFunctions.ALL_FUNCTIONS,
                    dataFlowParameters: convertParametersToArguments(this.dataflow.parameters),
                    dataFlowTransientColumns: convertColumnRefToDataFlowColumn(this.transientColumnRefs),
                    dataFlowCacheSinks: convertCacheSinksToArguments(this.dataflow.cacheSinks)
                }, this.customExpression);
            }
        }
    }

    private _createExpression(): DataFlowExpression {
        const expression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, this);
        expression.requiredReturnType = BooleanDataFlowType.TYPE;
        return expression;
    }
}
